import { InvalidFormSubmitEventName } from "@practical-computer/error-handling/event-handlers";
import { getMetaTag } from "../../utils/get-meta-tag";
import { FormAssociatedCustomElementsMixin } from "../mixins/form-associated-custom-elements";

const stripeKey = getMetaTag(`stripe-publishable-key`);
const stripeAccount = getMetaTag(`stripe-account-id`);

const reflectThemeChange = function () {
  document.querySelectorAll(`stripe-card-element`).forEach((x) => {
    x.updateStyle();
  });
};

window
  .matchMedia("(prefers-color-scheme: dark)")
  .addEventListener("change", reflectThemeChange);

const submitFormEvent = async function (event) {
  await event.currentTarget
    .querySelector(`stripe-card-element`)
    .confirmCardSetup(event);
};

class StripeCardElement extends FormAssociatedCustomElementsMixin(HTMLElement) {
  constructor() {
    super();
  }

  async connectedCallback() {
    if (!this.isConnected) {
      return;
    }

    if (!this.stripe) {
      this.stripe = Stripe(stripeKey.content, { stripeAccount: stripeAccount.content });
    }

    if (!this.cardElement) {
      const style = this.calculateStyle();

      this.cardElement = this.stripe.elements().create(`card`, {
        hidePostalCode: true,
        classes: {
          invalid: `wa-invalid`,
        },
        style,
      });
    }

    await this.cardElement.mount(this.elementMountpoint);
    this.updateStyle();
    this.resetConfirmed();

    this.form.addEventListener(`submit`, submitFormEvent);
  }

  async confirmCardSetup(event) {
    event.preventDefault();
    let clientSecret;

    this._internals.setValidity({});

    try {
      const response = await mrujs.fetch(this.setupIntentURL, {
        method: `POST`,
        headers: { "Content-Type": "application/json" },
      });

      const parsedResponse = await response.json();

      if (response.ok) {
        clientSecret = parsedResponse.clientSecret;
      } else {
        this._reflectCustomError(parsedResponse.error.message);
        return;
      }
    } catch (error) {
      this._reflectCustomError(this.stripeRequestError);
      return;
    }

    const result = await this.stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: this.cardElement },
    });

    if (result.error) {
      this._reflectCustomError(result.error.message);
    } else {
      this.value = result.setupIntent.payment_method;
      this.confirmedInput.value = "1";
      this.form.submit();
    }
  }

  calculateStyle() {
    const computedProperties = window.getComputedStyle(
      this.inputToPullStyleFrom,
    );

    const placeholderColor =
      computedProperties.getPropertyValue(`--placeholder-color`);
    const errorColor = computedProperties.getPropertyValue(`--error-color`);

    return {
      base: {
        iconColor: computedProperties.color,
        color: computedProperties.color,
        fontFamily: computedProperties.fontFamily,
        fontSize: computedProperties.fontSize,
        fontWeight: computedProperties.fontWeight,
        fontVariant: computedProperties.fontVariant,
        lineHeight: computedProperties.lineHeight,
        "::placeholder": {
          color: placeholderColor,
        },
      },
      invalid: {
        color: errorColor,
        iconColor: errorColor,
      },
    };
  }

  _updateFormValue(newValue) {
    super._updateFormValue(newValue);
    const event = new InputEvent(`change`, { bubbles: true });

    this.dispatchEvent(event);
  }

  _reflectCustomError(errorMessage) {
    this._internals.setFormValue("");
    this._internals.setValidity({ customError: true }, errorMessage);

    const event = new InputEvent(`change`, { bubbles: true });

    this.dispatchEvent(event);
    this.resetConfirmed();
    this.form.dispatchEvent(new CustomEvent(InvalidFormSubmitEventName));
  }

  updateStyle() {
    this.cardElement.update({ style: this.calculateStyle() });
  }

  reset() {
    this.value = "";
    this.resetConfirmed();
  }

  resetConfirmed() {
    this.confirmedInput.value = "0";
  }

  formResetCallback() {
    this.reset();
  }
  formStateRestoreCallback() {
    this.reset();
  }

  get setupIntentURL() {
    return this.getAttribute(`setup-intent-url`);
  }
  set setupIntentURL(value) {
    this.setAttribute(`setup-intent-url`, value);
  }
  get valueMissingError() {
    return this.getAttribute(`value-missing-error`);
  }
  get stripeRequestError() {
    return this.getAttribute(`stripe-request-error`);
  }
  get inputToPullStyleFrom() {
    return this.querySelector(`:scope > [data-input-to-pull-style-from]`);
  }
  get elementMountpoint() {
    return this.querySelector(`:scope > [data-stripe-card-element-mount]`);
  }
  get confirmedInput() {
    return this.querySelector(
      `:scope > [data-stripe-card-element-confirmed-input]`,
    );
  }
}

if (!window.customElements.get("stripe-card-element")) {
  window.customElements.define("stripe-card-element", StripeCardElement);
}
